import type { PromptSelectionFilter, PromptSelectionFilterGroup } from '../../services/api';
import type { SegmentData } from '../../services/segmentCache';
import type { ConditionFieldDefinition } from '../../config/conditionSearch';
import { extractFieldsFromResults, getMatchingMetadataFieldPaths, matchesMetadataConditions } from '../../utils/filterUtils';

export const getStudioConditionFields = (
  metadata: ReadonlyArray<Record<string, unknown> | undefined>,
): ConditionFieldDefinition[] => extractFieldsFromResults(
  metadata.map((value) => ({ extracted_metadata: value })),
).flatMap((field) => field.type === 'unknown' || field.searchable === false ? [] : [{
  path: field.name,
  label: field.label || field.name,
  type: field.type,
  itemType: field.itemType === 'object' ? undefined : field.itemType,
  description: field.description,
}]);

// Ad-hoc conditions narrow the chosen run result view. A level without
// conditions stays unconstrained; saved run filters are evaluated by the API.
export const selectStudioResults = (
  segments: readonly SegmentData[],
  videoMetadata: Record<string, unknown> | null,
  filter: PromptSelectionFilter | null,
): { segmentIds: Set<string>; videoMatched: boolean } => {
  if (!filter) return {
    segmentIds: new Set(segments.map((segment) => segment.segment_id)),
    videoMatched: videoMetadata !== null,
  };
  const videoMatched = filter.video ? Boolean(videoMetadata && matchesMetadataConditions(
    videoMetadata,
    filter.video.conditions.map((condition) => ({ ...condition, value: condition.value })),
    filter.video.logic,
  )) : videoMetadata !== null;
  const segmentGroup = filter.segment;
  const segmentConditions = segmentGroup?.conditions.map((condition) => ({ ...condition, value: condition.value })) || [];
  const segmentIds = new Set(segmentGroup ? segments.filter((segment) => (
    segment.metadata && segment.field_extraction_succeeded !== false
    && matchesMetadataConditions(segment.metadata, segmentConditions, segmentGroup.logic)
  )).map((segment) => segment.segment_id) : videoMatched
    ? segments.map((segment) => segment.segment_id) : []);
  if (filter.segment && filter.video && filter.level_logic !== 'or'
    && (!videoMatched || segmentIds.size === 0)) {
    return { segmentIds: new Set(), videoMatched: false };
  }
  return { segmentIds, videoMatched };
};

// A plain, case-insensitive substring search across field names and values.
export const studioSearchText = (metadata: unknown, text = ''): string => (
  `${JSON.stringify(metadata || {})} ${text}`.replace(/_/g, ' ').toLocaleLowerCase()
);

export const getStudioMatchedFieldPaths = (
  metadata: Record<string, unknown> | null | undefined,
  filter: PromptSelectionFilterGroup | null | undefined,
  query: string,
): Set<string> => {
  const matchedPaths = new Set<string>();
  if (!metadata) return matchedPaths;

  const filterPaths = filter ? getMatchingMetadataFieldPaths(
    metadata,
    filter.conditions.map((condition) => ({ ...condition, value: condition.value })),
    filter.logic,
  ) : new Set<string>();
  const normalizedQuery = query.trim().replace(/_/g, ' ').toLocaleLowerCase();
  if (!normalizedQuery && filterPaths.size === 0) return matchedPaths;

  const visit = (value: unknown, path: string, key?: string): boolean => {
    let matched = filterPaths.has(path);
    if (normalizedQuery) {
      // Include the key just as the full metadata search does, without matching
      // a neighboring field. Array values retain their exact instance paths.
      const text = JSON.stringify(key === undefined ? value : { [key]: value }) || '';
      matched = matched || text.replace(/_/g, ' ').toLocaleLowerCase().includes(normalizedQuery);
    }
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        matched = visit(item, `${path}[${index}]`) || matched;
      });
    } else if (value && typeof value === 'object') {
      Object.entries(value).forEach(([field, fieldValue]) => {
        matched = visit(fieldValue, path ? `${path}.${field}` : field, field) || matched;
      });
    }
    if (matched && path) matchedPaths.add(path);
    return matched;
  };
  visit(metadata, '');
  return matchedPaths;
};
